/**
 * Spreadsheet renderer (Library / file-preview).
 *
 * Renders a `kind: "spreadsheet"` artifact through the exact same
 * {@link SpreadsheetGrid} the chat artifact canvas uses — pinned header,
 * pinned row-number column, numeric alignment — so opening a spreadsheet
 * from the Library is pixel-identical to opening it in chat.
 *
 * This path is read-only: it resolves the persisted text via the source
 * adapter and parses it once. Renderers in this module never know how the
 * text was loaded; the source adapter (artifactSource, urlSource) handles
 * that.
 */
import { Loader2 } from "lucide-react";
import { useMemo } from "react";

import { SpreadsheetGrid } from "@/components/artifacts/spreadsheet-grid";
import { parseCsv } from "@/lib/artifacts/csv";

import { useFileText } from "../hooks/use-file-text";
import type { FileRendererProps } from "../types";

export function SpreadsheetRenderer({ source }: FileRendererProps) {
  const state = useFileText(source);

  const parsed = useMemo(
    () => parseCsv(state.status === "ready" ? state.text : ""),
    [state],
  );

  if (state.status === "loading" || state.status === "idle") {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="size-8 animate-spin text-primary" />
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
        <p>Failed to load spreadsheet.</p>
        <p className="text-xs opacity-70">{state.error.message}</p>
      </div>
    );
  }

  if (parsed.headers.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground italic">
        No data
      </div>
    );
  }

  return <SpreadsheetGrid parsed={parsed} className="h-full bg-muted/10" />;
}
